import React, { useState, useEffect, useCallback } from "react";
import "./toast.scss";
import { TickForToast } from "../../SVG/tick-for-toast";
import { ErrorForToast } from "../../SVG/error-for-toast";
import { GreenCrossIcon } from "../../SVG/green-cross";
import { RedCrossIcon } from "../../SVG/red-cross";

interface ToastData {
  id: string;
  type: "success" | "error";
  message: string;
  header?: string;
  duration: number;
}

interface ToastItemProps extends ToastData {
  onClose: (id: string) => void;
}

// Internal toast store
type ToastListener = (toasts: ToastData[]) => void;

class ToastStore {
  private toasts: ToastData[] = [];
  private listeners: Set<ToastListener> = new Set();
  private counter = 0;

  subscribe(listener: ToastListener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notify() {
    this.listeners.forEach((listener) => listener([...this.toasts]));
  }

  private generateId(): string {
    this.counter++;
    return `toast-${Date.now()}-${this.counter}`;
  }

  add(type: "success" | "error", message: string, header?: string, duration = 3000) {
    const newToast: ToastData = {
      id: this.generateId(),
      type,
      message,
      header,
      duration,
    };
    // Add new toast at the beginning (top of stack)
    this.toasts = [newToast, ...this.toasts];
    this.notify();
  }

  remove(id: string) {
    this.toasts = this.toasts.filter((t) => t.id !== id);
    this.notify();
  }

  getToasts() {
    return [...this.toasts];
  }
}

const toastStore = new ToastStore();

// Individual Toast Item
const ToastItem: React.FC<ToastItemProps> = ({
  id,
  type,
  message,
  header,
  duration,
  onClose,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => {
      onClose(id);
    }, 300);
  }, [id, onClose]);

  useEffect(() => {
    // Trigger enter animation
    const showTimeout = setTimeout(() => setIsVisible(true), 10);

    // Auto-dismiss after duration
    const hideTimeout = setTimeout(() => {
      handleClose();
    }, duration);

    return () => {
      clearTimeout(showTimeout);
      clearTimeout(hideTimeout);
    };
  }, [duration, handleClose]);

  const iconSrc = type === "success" ? <TickForToast /> : <ErrorForToast />;
  const closeIcon = type === "success" ? <GreenCrossIcon /> : <RedCrossIcon />;
  const containerClass = type === "success" ? "success" : "error";

  return (
    <div
      className={`custom-toast-item ${containerClass} ${isVisible ? "show" : ""} ${isExiting ? "exiting" : ""} ${header ? "has-header" : ""}`}
    >
      <div className="custom-toast-icon">{iconSrc}</div>
      <div className={`custom-toast-content ${containerClass}`}>
        {header && <div className="content-header">{header}</div>}
        <div className={header ? "content-message" : "content-header"}>{message}</div>
      </div>
      <div className="custom-toast-icon close" onClick={handleClose}>
        <span className="alert-icons">{closeIcon}</span>
      </div>
    </div>
  );
};

// Toast Container - renders all active toasts
export const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  useEffect(() => {
    return toastStore.subscribe(setToasts);
  }, []);

  const handleClose = useCallback((id: string) => {
    toastStore.remove(id);
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div className="toast-container">
      {toasts.map((toastData) => (
        <ToastItem
          key={toastData.id}
          {...toastData}
          onClose={handleClose}
        />
      ))}
    </div>
  );
};

/**
 * Toast API - Simple way to show toast notifications
 * 
 * Usage:
 * ```tsx
 * // Add ToastContainer once in your app root
 * <ToastContainer />
 * 
 * // Then call toast.success or toast.error anywhere
 * toast.success("Changes saved!");
 * toast.error("Something went wrong");
 * 
 * // With optional header
 * toast.success("Your file has been uploaded", "Upload Complete");
 * 
 * // With custom duration (ms)
 * toast.success("Quick message", undefined, 1500);
 * ```
 */
export const toast = {
  /**
   * Show a success toast
   * @param message - The message to display
   * @param header - Optional header text
   * @param duration - How long to show the toast (default: 3000ms)
   */
  success: (message: string, header?: string, duration?: number) => {
    toastStore.add("success", message, header, duration);
  },

  /**
   * Show an error toast
   * @param message - The message to display
   * @param header - Optional header text
   * @param duration - How long to show the toast (default: 3000ms)
   */
  error: (message: string, header?: string, duration?: number) => {
    toastStore.add("error", message, header, duration);
  },
};
